import type { SourceType } from '@/feature/patients/types';

export type MedicationDataType =
  | 'problems'
  | 'medications'
  | 'labResults'
  | 'procedures'
  | 'allergies'
  | 'vaccinations'
  | 'vitals';

export interface MedicationDataCounts {
  problems: number;
  medications: number;
  labResults: number;
  procedures: number;
  allergies: number;
  vaccinations: number;
  vitals: number;
}

export interface MedicationDataTypeConfig {
  key: MedicationDataType;
  label: string;
  countKey: keyof MedicationDataCounts;
}

// Record interfaces per category — aligned to API field names

export interface ProblemDataRecord {
  id: string;
  name: string | null;
  onsetDate?: string;
  createdAt: string;
  sourceTypes: SourceType[];
}

export interface ProblemDataCuratedRow {
  id: string;
  name: string | null;
  onsetDate?: string;
  clinicalStatus?: string;
  conditionNote?: string;
  recordedDate?: string;
  dateUploaded?: string;
}

export interface ProblemDataDetailsResponse {
  problem: ProblemDataCuratedRow;
}

// Aligned to medicalDataRouter.getPatientMedicationsSummaries output.
export interface MedicationDataRecord {
  id: string;
  name: string | null;
  dosage?: string;
  createdAt: string | Date;
  sourceTypes: SourceType[];
}

// Aligned to medicalDataRouter.getPatientAllMedicationData output (curated row).
export interface MedicationAllDataPractitioner {
  referenceId?: string;
  givenName?: string;
  familyName?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressUse?: string;
  roleSpecialty?: string;
  role?: string;
  telecoms: string[];
}

export interface MedicationAllDataRecord {
  id: string;
  name: string | null;
  dosage?: string;
  createdAt: string | Date;
  text?: string;
  status?: string;
  startDatetime?: string;
  endDatetime?: string;
  doseUnit?: string;
  doseRoute?: string;
  doseValue?: string;
  patientInstructions?: string;
  practitioner?: MedicationAllDataPractitioner;
}

export interface LabResultDataRow {
  labTest: string | null;
  value: string | null;
  units?: string;
  dateOfStudy: string | null;
  sourceTypes: SourceType[];
}

export interface LabResultDataRecord {
  id: string;
  labPanel: string | null;
  rows: LabResultDataRow[];
}

export interface LabResultDataFlatRow {
  panelId: string;
  labPanel: string | null;
  labTest: string | null;
  value: string | null;
  units?: string;
  dateOfStudy: string | null;
  sourceTypes: SourceType[];
}

export const flattenLabResultPanels = (
  panels: LabResultDataRecord[],
): LabResultDataFlatRow[] =>
  panels.flatMap((panel) => {
    if (panel.rows.length === 0) {
      return [
        {
          panelId: panel.id,
          labPanel: panel.labPanel,
          labTest: '',
          value: '',
          units: undefined,
          dateOfStudy: '',
          sourceTypes: [],
        },
      ];
    }
    return panel.rows.map((row) => ({
      panelId: panel.id,
      labPanel: panel.labPanel,
      labTest: row.labTest,
      value: row.value,
      units: row.units,
      dateOfStudy: row.dateOfStudy,
      sourceTypes: row.sourceTypes,
    }));
  });

export interface LabDataPractitioner {
  referenceId?: string;
  givenName?: string;
  familyName?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressUse?: string;
  roleSpecialty?: string;
  role?: string;
  telecoms: string[];
}

export interface LabDataPanelRow {
  labTest: string | null;
  value: string | null;
  units?: string;
  dateOfStudy?: string;
  interpretation?: string;
}

export interface LabDataPanel {
  id: string;
  labPanel: string | null;
  dateOfStudy: string | null;
  rows: LabDataPanelRow[];
  interpreters: LabDataPractitioner[];
  performers: LabDataPractitioner[];
}

export interface LabResultDataDetailsResponse {
  labPanel: LabDataPanel;
}

export interface ProcedureDataRecord {
  id: string;
  name: string | null;
  date?: string | null;
  providerType?: string | null;
  sourceTypes: SourceType[];
}

export interface AllergyDataRecord {
  id: string;
  name: string | null;
  reaction: string;
  sourceTypes: SourceType[];
}

export interface VaccinationDataRecord {
  id: string;
  name: string | null;
  dateAdministered: string | null;
  sourceTypes: SourceType[];
}

export interface VitalDataRecord {
  id: string;
  createdAt: string;
  name: string | null;
  value: string | null;
  units?: string;
  dateOfStudy: string | null;
  sourceTypes: SourceType[];
}

export interface VitalDataCuratedRow {
  id?: string;
  groupingObservationId?: string;
  category?: string;
  name: string | null;
  text?: string;
  observedAt: string | null;
  unit?: string;
  value: string | null;
  dateUploaded?: string;
  vitalSignId?: string;
  observationCode?: string;
  observationCodeSystem?: string;
}

export interface VitalDataDetails {
  groupingObservationId?: string;
  vitals: VitalDataCuratedRow[];
}

export type AnyMedicationDataRecord =
  | ProblemDataRecord
  | MedicationDataRecord
  | LabResultDataRecord
  | ProcedureDataRecord
  | AllergyDataRecord
  | VaccinationDataRecord
  | VitalDataRecord;
